const addonsKey = (item) => {
  const addons = item?.selectedAddons?.length > 0 ? item.selectedAddons : item?.addons;
  if (!Array.isArray(addons) || addons.length === 0) return "";
  return addons
    .map((addon) => `${addon?.mProductAddOnId ?? addon?.productId ?? addon?.id ?? ""}:${addon?.qty ?? addon?.addonQty ?? addon?.quantity ?? 1}`)
    .sort()
    .join("|");
};

// Lines that must never be silently collapsed, even if their signature matches another line.
const isMergeable = (item) =>
  item?.addNewLine !== "Y" &&
  !(item?.childItems?.length > 0) &&
  !(item?.children?.length > 0) &&
  item?.giftCardType !== "DIG" &&
  !item?.isGiftCard;

// Includes qty (unlike consolidateBillItems.js's mergeKey, which deliberately excludes qty so
// different-quantity fire batches of the same product can be summed). Here we're identifying
// byte-identical duplicate rows, so qty must match too.
const signature = (item) =>
  [
    item.productId || item.mProductId || "",
    item.upc || "",
    item.mBatchId || "",
    item.uom || item.csUomId || "",
    item.isReturn ? "R" : "S",
    item.qty ?? item.weight ?? "",
    item.realPrice ?? item.salePrice ?? "",
    item.sunitprice ?? "",
    item.listPrice ?? "",
    item.taxRate ?? "",
    item.taxFlag || "",
    item.discountType || "",
    item.discountName || "",
    item.mPricingruleId || "",
    (item.notes || "").trim(),
    addonsKey(item),
  ].join("::");

// BUG-1920: Sales History reconstructs an order's items 1:1 from the backend's `line` array.
// A concurrent Mobile App + Cashier POS write (same trigger class as BUG-1791) can persist the
// exact same order line twice under two different sOrderlineIDs — a pure duplicate-insert
// artifact, not a second real unit. The order's own aggregate fields (grosstotal/cwrProductQty)
// are computed once from the real sale and stay correct even when the child `line` array is
// doubled.
//
// A byte-identical second line is indistinguishable, at the line level, from a genuine second
// real order of the same item at the same price placed later in the same order — so we can't
// just drop duplicates unconditionally. Instead we use totalQty (cwrProductQty) as a ground
// truth: only collapse duplicate-signature lines down to one copy each when doing so makes the
// reconciled quantity match totalQty exactly. If it doesn't reconcile, leave the data untouched
// — better to show a duplicate than to silently drop a paid-for line.
export const reconcileDuplicateOrderLines = (items, totalQty) => {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (totalQty === undefined || totalQty === null) return items;

  const seen = new Set();
  const deduped = [];
  items.forEach((item) => {
    if (!isMergeable(item)) {
      deduped.push(item);
      return;
    }
    const key = signature(item);
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(item);
  });

  if (deduped.length === items.length) return items;

  const dedupedQty = deduped.reduce((sum, item) => sum + (Number(item.qty ?? item.weight) || 0), 0);
  return dedupedQty === Number(totalQty) ? deduped : items;
};
